import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import type { SenderGroupInfo } from '@/features/email-list/types';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';

export type SenderDescriptor = { contactId: string } | { email: string };

export interface SenderContext {
  accountId: string;
  folderId: string;
}

/**
 * Resolve the descriptor the senders API needs. Always prefers the email
 * address: contactIds are account-specific and break when the same sender has
 * mail in multiple accounts.
 */
export function resolveSenderDescriptor(group: SenderGroupInfo): SenderDescriptor | null {
  const normalizedEmail = group.senderEmail?.toLowerCase()?.trim();
  if (normalizedEmail) {
    return { email: normalizedEmail };
  }
  const fallbackEmail = group.emails[0]?.from?.email?.toLowerCase()?.trim();
  if (fallbackEmail) {
    return { email: fallbackEmail };
  }
  if (group.contactId) {
    return { contactId: group.contactId };
  }
  return null;
}

/**
 * THE sender-context resolver (one source of truth for "which real
 * (account, folder) pairs does this sender group span?").
 *
 * In the unified view this fans out across ALL accounts — via the unified
 * folder's sourceFolders when loaded, else by role for every actual account —
 * because the group's loaded emails are just one page and may only contain one
 * account's messages. Loaded-emails-only context gathering was the root cause
 * of unified group actions silently skipping accounts (audit C1/C5/A7/B15).
 *
 * In a single-account view it gathers contexts from the group's emails and
 * falls back to the active selection. Unified folder ids are never returned.
 */
export function resolveSenderGroupContexts(
  group: SenderGroupInfo,
  folderIdOverride?: string | null
): Map<string, SenderContext> {
  const folderState = useFolderStore.getState();
  const accountState = useAccountStore.getState();
  const folderId = folderIdOverride ?? folderState.selectedFolderId();
  const selectedAccountId = accountState.selectedAccountId;
  const isUnifiedView = selectedAccountId === UNIFIED_ACCOUNT_ID;

  const contexts = new Map<string, SenderContext>();
  const add = (accountId: string | null | undefined, ctxFolderId: string | null | undefined) => {
    if (!accountId || !ctxFolderId) {
      return;
    }
    const key = `${accountId}:${ctxFolderId}`;
    if (!contexts.has(key)) {
      contexts.set(key, { accountId, folderId: ctxFolderId });
    }
  };

  if (isUnifiedView && folderId) {
    const folder = folderState.folders.find((f) => f.id === folderId);
    if (folder?.sourceFolders?.length) {
      folder.sourceFolders.forEach((source) => add(source.accountId, source.folderId));
    } else if (folderId.startsWith(`${UNIFIED_ACCOUNT_ID}:`)) {
      // Folders not loaded yet — fan out by role (the server resolves
      // mailboxes by role name) across every actual account.
      const role = folderId.slice(`${UNIFIED_ACCOUNT_ID}:`.length);
      accountState.actualAccounts().forEach((account) => add(account.id, role));
    }
  }

  // Contexts from the group's loaded emails (covers single-account view and
  // any folder a loaded message actually lives in). Skip unified ids — a
  // loaded email's folderId is always real, but the fallback below isn't.
  group.emails.forEach((email) => {
    if (email.folderId && !email.folderId.startsWith(`${UNIFIED_ACCOUNT_ID}:`)) {
      add(email.accountId, email.folderId);
    }
  });

  // Single-account fallback when nothing else resolved.
  if (
    !contexts.size &&
    folderId &&
    selectedAccountId &&
    selectedAccountId !== UNIFIED_ACCOUNT_ID &&
    !folderId.startsWith(`${UNIFIED_ACCOUNT_ID}:`)
  ) {
    add(selectedAccountId, folderId);
  }

  return contexts;
}
